import Web3 from 'web3';
import contractData from '../web3/hostContract.json';
import hostRegistry from '../web3/hostRegistry.json';
import config from '../web3/web3Config.json';
import Cookies from "universal-cookie";
import { generatePresignedUrl, getEncryptedValue, getForwardEmailDetails, isApiError, sendAttachment, sendWebTwoEmail, storeSendCopyEmailsInAdmin, updateInsecureMail } from '../service/api-actions';
import { transactionAction } from './chain-helper';
import db from '../db/db-service.js';
import { addOutbox, deleteOutboxAt, readOrganizationDataStrict } from '../db/at-rest.js';
import { getHostHelperContract, getHostContract } from './contract-helper.js';
import { toast, Bounce } from 'react-toastify';
import { generateEvent, parseICalendar } from './inviteHelper.js'
import constants from '../constants/constants.json';
import { getGeneralSettings } from './settingsHelper.js';
import { contractCall } from './web3helper.js';
import { AttachmentUploadError, encryptFilesV3, encryptFilesWithDek, encryptText, readFilesAsBase64 } from './attachmentHelper.js';
import { getSessionDEK, isDekWriteEnabled, symEncrypt } from './dekManager.js';
import { ENVELOPE_VERSION_V3, EnvelopeV3Error, isV3WriteEnabled, mintK, sealV3, wrapK } from './envelopeV3.js';
import { reportError, toErrorString } from '../utils/errorLogger.js';

const contractAddress = config.CONTRACT;
const chainId = config.CHAIN_ID;
const cookies = new Cookies();

const contractMethods = contractCall(contractData.contract, contractAddress);


/**
 * Raised when a copy of the mail could not be encrypted, so the whole send must be abandoned.
 *
 * WHY THIS EXISTS: /getEncryptValue does not throw on a 429, a 500 or a dropped connection — fetchAPI
 * returns the {__error:true} sentinel instead (see isApiError in service/api-actions.js). That sentinel is
 * a truthy OBJECT, so the old `return data && data.returnValue` handed back undefined,
 * getEncryptedMessageByUserArray turned undefined into "-", and the "-" filter in sendEmails then stripped
 * that recipient AND their ciphertext out of the transaction — while the Sent copy's header still listed
 * them and the user was told "Message Sent". A recipient silently deleted from a mail the sender believes
 * was delivered is the worst outcome available here, so a failed call now aborts the entire send and names
 * the addresses it could not encrypt for.
 *
 * A recipient with NO on-chain public key is NOT this error. That is an ordinary web2/external address: it
 * leaves through the `if (!publicKey) return null` branch of getEncryptedMessageByUser without ever calling
 * /getEncryptValue, and is delivered by the sendWebTwoEmail path below. Only a FAILED CALL lands here.
 */
export class EncryptionFailedError extends Error {
    constructor(addresses, reason) {
        super(`Message encryption failed for ${addresses.join(", ")}: ${reason}`);
        this.name = 'EncryptionFailedError';
        this.addresses = addresses;
        this.reason = reason;
    }
}

/**
 * Unwrap a /getEncryptValue reply or abort the send. `address` is whose copy this is, so the user can be
 * told who was affected.
 *
 * Rejects an empty returnValue as well as the sentinel: a blank ciphertext is filtered out downstream by
 * exactly the same "-" rule and would drop the recipient just as silently.
 */
function requireEncryptedValue(data, address) {
    if (isApiError(data)) {
        throw new EncryptionFailedError([address], data.message || 'the encryption service could not be reached');
    }
    if (!data || !data.returnValue) {
        throw new EncryptionFailedError([address], 'the encryption service returned no ciphertext');
    }
    return data.returnValue;
}


/**
 * What a caller passes as `defaultEncryptedMessage` to mean "I have NOT sealed the Sent copy — seal it for
 * me". It is a REQUEST SENTINEL, not ciphertext: sendEmails answers it by calling saveSenderEncryptedEmail()
 * itself (see the isSavedOn branch below).
 *
 * DO NOT "fix" this by making it unique per send the way the mobile composer's initializer is. Mobile's
 * literal is a FALLBACK — the value that gets WRITTEN when sealing fails — so uniqueness is what stops it
 * colliding. This one is a SWITCH that sendEmails compares against; a per-send value would simply never
 * match, and the Sent copy would silently stop being sealed at all. What protects the chain here is
 * isSenderCopyPlaceholder() refusing to write ANY placeholder, which holds no matter what this string is.
 */
export const SENDER_COPY_SENTINEL = "MSG";

/**
 * True when `value` is a marker meaning "encryption did not happen", rather than real ciphertext.
 *
 * WHY THIS EXISTS — writing a placeholder here is a permanent CHAIN-WIDE outage, not a local glitch.
 * storeSendMail passes this string as `_encryptedData[0]`, and newHostContract.createEmail enforces
 *     require(!encryptedDataExists[_encryptedData[i]], "Encrypted data already exists.");
 * (newHostContract.sol :381, recorded at :412) GLOBALLY and PERMANENTLY. So a FIXED placeholder — the
 * literal "MSG" that `resolvedSenderEncryptedMessage || defaultEncryptedMessage` used to fall back to — is
 * claimed by the first account on the chain ever to hit the failure path, and from then on every other
 * user's send of that same string REVERTS.
 * transactionAction returns null on a revert and the storeSendMail call below ignored it, so the symptom
 * was Sent copies quietly vanishing, for everyone, with the user told "Message Sent".
 *
 * And for the one account that got there first it is just as bad: the placeholder IS the Sent row's body —
 * immutable, undecryptable, and presented in Sent as a real archived message.
 *
 * The MSG_ rule also covers the placeholder families the mobile client produces (MSG_ENCRYPTED_FALLBACK_,
 * MSG_NO_KEY_, MSG_EMPTY_RESULT_, MSG_ENCRYPT_FAILED_, MSG_ERROR_) and any future uniquified variant of the
 * same idea. Real ciphertext that happened to begin "MSG_" would be rejected too; that costs one skipped
 * Sent copy plus an honest message, versus a permanently unreadable row and a chain-wide revert — the right
 * way round.
 */
function isSenderCopyPlaceholder(value) {
    if (typeof value !== 'string') return true;
    const trimmed = value.trim();
    if (!trimmed || trimmed === '-' || trimmed === 'null' || trimmed === 'undefined') return true;
    return /^MSG(_|$)/i.test(trimmed);
}

/**
 * The one place the user is told their Sent copy did not make it.
 *
 * The mail itself WAS delivered by the time this fires, and the caller is about to show its own
 * "Message Sent" toast, so this is worded as an addition to that rather than a contradiction of it. It is
 * raised from here, not from the composer, so every send path gets it — ComposeEmail, the reply modal in
 * common-alert.js and AddEmailModal all call sendEmails and none of them can see why the archive was
 * skipped. Own toastId so it never displaces the "sendFailure" messages, which mean the opposite.
 */
function warnSenderCopyNotSaved(reason) {
    reportError({
        endpoint: '-',
        message: `FileName: emailHelper.js, Fun: sendEmails, Sent copy not archived: ${reason}`,
        error: reason,
        level: 'warn',
        userName: localStorage.getItem('UserName') || '',
    });
    toast.warn(
        "Your message was delivered, but a copy could not be saved to your Sent folder this time. Please keep your own copy if you need one.",
        {
            toastId: "sentCopyNotSaved",
            position: "top-center",
            transition: Bounce,
        }
    );
}


/**
 * The sender's OWN saved copy of a sent mail ("Save sent items").
 *
 * This is single-owner data: it is encrypted to us, stored for us, and read back only by us
 * (Messages.js / Encrypt.js). So under the DEK cutover it is sealed symmetrically — one wallet unwrap per
 * session instead of an asymmetric op per sent mail — and tagged version 'v2', which is exactly what the
 * read side already branches on.
 *
 * The branch lives HERE, at the call site, and not inside encryptText: that primitive is shared with
 * getEncryptedMessageByUser, which encrypts to the RECIPIENT's public key. Nothing recipient-bound may ever
 * be DEK-sealed — it would be permanently unreadable to every recipient, unrecoverably.
 *
 * Default OFF (web3Config.DMAIL_DEK_WRITE): until every client ships the dual-read, no v2 rows are minted.
 */
export async function saveSenderEncryptedEmail(emailObject) { // Save sent items.
    try {
        const userName = cookies.get("userObject");
        const msg = JSON.stringify(emailObject);
        let encryptMsg;
        if (isDekWriteEnabled()) {
            // 'v2' discriminates the ROW; the WASM itself is always handed 'v1' (see dekManager).
            encryptMsg = { encryptedData: await symEncrypt(await getSessionDEK(), msg), version: 'v2' };
        } else {
            const publicKey = await contractMethods.methods.getPublicKeyOfUser(userName.name).call();
            encryptMsg = await encryptText(msg, publicKey);
        }
        const data = await getEncryptedValue(encryptMsg);
        // Must not degrade. On a failed call `data.returnValue` was undefined, and sendEmails substituted
        // its placeholder `defaultEncryptedMessage` — so the literal string "MSG" was written on-chain as
        // the body of the Sent mail, permanently, while the user was told "Message Sent".
        const encryptedMessage = requireEncryptedValue(data, userName?.name || 'your own saved copy');
        return encryptedMessage;
    } catch (err) {
        // Abort the send rather than returning undefined and letting "MSG" be stored as the body.
        if (err instanceof EncryptionFailedError) throw err;
        // EVERYTHING ELSE still returns undefined, deliberately — a getPublicKeyOfUser RPC failure, or
        // getSessionDEK / symEncrypt / encryptText throwing. This function is also called directly by the
        // reply modal (modals/common-alert.js), whose click handler has no try/catch, so throwing there
        // would strand its loader forever. Returning undefined is safe now only because sendEmails no
        // longer turns it into a placeholder: it refuses to archive at all and tells the user.
        //
        // Reported because that message can only say the copy was not saved — this is the sole record of WHY.
        reportError({
            endpoint: '-',
            message: `FileName: emailHelper.js, Fun: saveSenderEncryptedEmail, ${String(err?.message || 'Sent copy could not be encrypted')}`,
            error: toErrorString(err || err?.message),
            level: 'warn',
            userName: localStorage.getItem('UserName') || '',
        });
        console.log(err);
    }
}


const validateEmails = (emails) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));
    return {
        isValid: invalidEmails.length === 0,
        invalidEmails
    };
};

export const sendInvite = async (bulkemail, eventParams, active, account, subject, text, files) => {
    try {
        let event = null;
        let creatEvent = null;
        let file = null;
        if (eventParams) {
            creatEvent = await generateEvent(eventParams, active);
            if (!creatEvent) {
                return false;
            }
            const blob = new Blob([creatEvent], { type: "text/calendar" });
            file = new File([blob], "invite.ics", { type: "text/calendar" });
            event = await parseICalendar(creatEvent);
            if (!event) {
                return false;
            }
        }
        // Same sentinel value as before, named rather than re-typed. isSavedOn is false here, so no Sent
        // copy is written for an invite either way.
        return await sendEmails(bulkemail, [], [], subject, text, false, SENDER_COPY_SENTINEL, [file], event)
    } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '-',
            message: `FileName: emailHelper.js, Fun: sendInvite, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        console.log(error);
        return null;
    }
}

// function to send/store email on blockchain
export const sendEmails = async (to, cc, bcc, subject, message, isSavedOn = false, defaultEncryptedMessage = "MSG", files = null, event = null, senderName = null, fromAddress = null) => {

    const allUsers = to.concat(cc, bcc).filter(value => value !== "");
    if (!senderName) {
        const settings = await getGeneralSettings();
        if (settings?.isSecureSent) {
            if (allUsers.length !== 1) {
                toast.error("Send Secure Email is enabled, so only one recipient is allowed. Please specify a single recipient.", {
                    position: "top-center",
                    transition: Bounce,
                });
                return false;
            }
        }
    }

    if (allUsers.length === 0) {
        toast.error("Please specify at least one recipient.", {
            position: "top-center",
            transition: Bounce,
        });
        return false;
    }
    const { isValid, invalidEmails } = validateEmails(allUsers);
    if (!isValid) {
        toast.dismiss()
        const errorMessage = `Invalid email ${invalidEmails.length > 1 ? 'addresses' : 'address'}: ${invalidEmails.join(", ")}`;
        toast.error(errorMessage, {
            position: "top-center",
            transition: Bounce,
        });
        return false;
    }

    const userRestrictions = await organizationEmailValidation(allUsers);
    if (userRestrictions) {
        toast.error('You are restricted from sending emails to external organizations or domains.',
            {
                position: "top-center",
                transition: Bounce,
            }
        );
        return false;
    }

    const unIdentifiedUser = await userAvailableCheck(allUsers);
    let allForwardEmails = [];
    let allAliasesEMails = [];
    let replaceAliaseUsers = [];
    let forwardUnIdentifiedUsers = [];
    let aliasesUnIdentifiedUsers = [];
    if (allUsers?.length !== unIdentifiedUser?.length && !senderName) {
        const { forwardEmails, aliasesEmails } = await forwardEmailsCheck(allUsers);
        forwardUnIdentifiedUsers = await userAvailableCheck(forwardEmails);
        allForwardEmails = forwardEmails.filter(email => !forwardUnIdentifiedUsers.includes(email));
        const filterAliase = aliasesEmails?.map((email) => (email?.primary));
        replaceAliaseUsers = aliasesEmails?.map((email) => (email?.email));
        aliasesUnIdentifiedUsers = await userAvailableCheck(filterAliase);
        allAliasesEMails = filterAliase.filter(email => !aliasesUnIdentifiedUsers.includes(email));
    }

    const userNameData = cookies.get("userObject");
    let sender = senderName ? senderName : userNameData?.name
    const outbox = {
        to: to,
        bcc: bcc,
        cc: cc,
        subject: subject || "(No Subject)",
        message: message,
        sender: fromAddress || sender,
    }
    try {
        sender = fromAddress || sender;
        const emailHeaderObject = { sender, to, cc, bcc: [], subject };
        const senderEmailHeaderObject = { sender, to, cc, bcc, subject };

        let filterTo = to, filterCC = cc, filterBcc = bcc;
        if (replaceAliaseUsers?.length) {
            filterTo = filterTo?.filter(email => !replaceAliaseUsers.includes(email));
            filterCC = filterCC?.filter(email => !replaceAliaseUsers.includes(email));
            filterBcc = filterBcc?.filter(email => !replaceAliaseUsers.includes(email));
        }

        const UTCtime = new Date().toUTCString();

        // Read each attachment to base64 ONCE for the whole send, instead of once per recipient. The
        // ciphertext still has to be produced and uploaded per recipient (each copy carries a wrapped key
        // bound to that one recipient), but the plaintext read is identical every time and, for large files,
        // is the expensive half. Send-scoped: this Map holds attachment plaintext and dies with the send.
        // v3 only — the v1 path is left byte-for-byte and code-for-code as it was.
        const fileBase64ByFile = (isV3WriteEnabled() && files && files.length)
            ? await readFilesAsBase64(files)
            : null;

        const encryptionPromises = [
            getEncryptedMessageByUserArray(filterTo, subject, message, files, event, fileBase64ByFile),
            getEncryptedMessageByUserArray(filterCC, subject, message, files, event, fileBase64ByFile),
            getEncryptedMessageByUserArray(filterBcc, subject, message, files, event, fileBase64ByFile),
        ];

        let senderEncryptedMessagePromise = Promise.resolve(defaultEncryptedMessage);
        // Was `defaultEncryptedMessage === "MSG" || !defaultEncryptedMessage`. Widened to the full
        // placeholder test so that a caller which already TRIED to seal the Sent copy and got a placeholder
        // back gets it sealed here instead: modals/common-alert.js calls saveSenderEncryptedEmail() itself
        // and passes on whatever it returned, which is undefined when that call failed. Anything that is
        // real ciphertext still goes through untouched.
        if (isSavedOn && isSenderCopyPlaceholder(defaultEncryptedMessage)) {
            const emailObject = { recipient: to.join(', '), subject: subject, message: message };
            const walletAddress = localStorage.getItem("walletAddress");
            senderEncryptedMessagePromise = (async () => {
                if (files && files.length) {
                    // The sender's OWN copy of the attachments — a SECOND, separate upload. The
                    // recipients' copies are uploaded independently by getEncryptedMessageByUser below,
                    // against each recipient's own public key. Only this copy may be DEK-sealed, and
                    // branching here at the call site is what guarantees the recipient path never can be:
                    // sendAttachment / encryptFiles / encryptText are shared and stay untouched.
                    let attachmentResult;
                    if (isDekWriteEnabled()) {
                        attachmentResult = await encryptFilesWithDek(files);
                    } else {
                        const publicKey = await contractMethods.methods.getPublicKeyOfUser(sender).call();
                        attachmentResult = await sendAttachment(files, publicKey);
                    }
                    emailObject.attachment = attachmentResult.returnValue;
                }
               return await saveSenderEncryptedEmail(emailObject, walletAddress);
            })();
        }

        const [
            toEncryptionMessage,
            ccEncryptionMessage,
            bccEncryptionMessage,
            resolvedSenderEncryptedMessage
        ] = await Promise.all([
            ...encryptionPromises,
            senderEncryptedMessagePromise
        ]);

        const finalSenderEncryptedMessage = resolvedSenderEncryptedMessage || defaultEncryptedMessage;

        // The ONLY value allowed to be written as the Sent copy's body — null when there isn't one.
        //
        // `finalSenderEncryptedMessage` degrades to `defaultEncryptedMessage` (the "MSG" sentinel) whenever
        // saveSenderEncryptedEmail swallowed a non-EncryptionFailedError and returned undefined, and that
        // sentinel used to be handed to storeSendMail as `_encryptedData[0]` — see isSenderCopyPlaceholder
        // for why one fixed string reaching that argument breaks Sent copies for every account on the chain.
        // Nothing is written instead: an undecryptable row in the user's own Sent folder is not a copy, and
        // it can never be repaired once on chain.
        const senderCopyCiphertext = isSenderCopyPlaceholder(finalSenderEncryptedMessage)
            ? null
            : finalSenderEncryptedMessage;

        const forwardEncryptedMessages = [];
        if (allForwardEmails?.length > 0) {
            const forwardPromises = allForwardEmails.map(async (user) => {
                 if (user) {
                    const toUser = filterTo?.join(', ');
                    const encryptedMessage = await getEncryptedMessageByUser(toUser, subject, message, files, event, user, fileBase64ByFile);
                    return encryptedMessage || "-";
                }
                return "-";
            });
            const results = await Promise.all(forwardPromises);
            forwardEncryptedMessages.push(...results);
        }

        const forwardAliaseEncryptedMessages = [];
        if (allAliasesEMails?.length > 0) {
            const aliasPromises = allAliasesEMails.map(async (user) => {
                 if (user) {
                    const toUser = filterTo?.join(', ');
                    const encryptedMessage = await getEncryptedMessageByUser(toUser, subject, message, files, event, user, fileBase64ByFile);
                    return encryptedMessage || "-";
                }
                return "-";
            });
             const results = await Promise.all(aliasPromises);
             forwardAliaseEncryptedMessages.push(...results);
        }

        const accountAddress = localStorage.getItem("walletAddress");
        const walletAddress = senderName ? '' : accountAddress;
        
        // Index 3 is carried for historical shape only — createEmail() reads _emailDetails[1] (receivedDate)
        // and _emailDetails[2] (the header JSON) and nothing else, so this element is never stored, never
        // registered in encryptedDataExists, and never seen by a recipient. That is why the placeholder
        // problem lives entirely on the storeSendMail write below, and why DELIVERY is left byte-for-byte
        // as it was.
        const emailDetails = [walletAddress, UTCtime, JSON.stringify(emailHeaderObject), finalSenderEncryptedMessage];
        const senderEmailDetails = [walletAddress, UTCtime, JSON.stringify(senderEmailHeaderObject), finalSenderEncryptedMessage];

        const functionParams = [subject, sender, filterTo, filterCC, filterBcc, toEncryptionMessage, ccEncryptionMessage, bccEncryptionMessage, emailDetails];
        let filteredTo = functionParams[2].filter((val, index) => functionParams[5][index] !== '-');
        let filteredCC = functionParams[3].filter((val, index) => functionParams[6][index] !== '-');
        let filteredBcc = functionParams[4].filter((val, index) => functionParams[7][index] !== '-');
        let filterEncryptTo = functionParams[5].filter(val => val !== '-');
        let filterEncryptCC = functionParams[6].filter(val => val !== '-');
        let filterEncryptBcc = functionParams[7].filter(val => val !== '-');

        const combainedForwardAliasesUsers = [...allForwardEmails, ...allAliasesEMails]
        const isOwnDomainPresent = await getOwnDomainBoolStatus([filteredTo, filteredCC, filteredBcc, combainedForwardAliasesUsers]);
        filteredTo = filteredTo.concat(combainedForwardAliasesUsers).filter(email => email !== "");
        filterEncryptTo = filterEncryptTo.concat(forwardEncryptedMessages).filter(email => email !== "-");
        filterEncryptTo = filterEncryptTo.concat(forwardAliaseEncryptedMessages).filter(email => email !== "-");

        const filteredParams = [filteredTo, filteredCC, filteredBcc, filterEncryptTo, filterEncryptCC, filterEncryptBcc, sender, emailDetails, subject, constants.emailAttributes]
        let checkIsSaved = false;

        if (isOwnDomainPresent) {
            checkIsSaved = true;
            const returnValue = await saveEmailForUser(walletAddress, filteredParams); // send emails
            if (!returnValue) {
                if (!senderName) await setOutBoxDB(outbox);
                return false
            }
        }

        // ── SMTP (web2) DELIVERY SET ──────────────────────────────────────────────────────────────────
        //
        // EVERY address that reaches sendWebTwoEmail is DELIVERED a CLEARTEXT copy of `message`. The server
        // (DSuite.Emails.Api SendEmailAsync) builds ONE MimeMessage out of these to/cc/bcc fields and hands
        // it to MailKit with no explicit recipient list, so the SMTP envelope — the RCPT TO — is derived
        // from those very headers. Passing the full `to`/`cc`/`bcc` here therefore mailed a plaintext
        // duplicate of the message to every on-chain dMail recipient who had just received it end-to-end
        // encrypted, and it was DELIVERED rather than bounced: the server rewrites DMAIL_DOMAIN to
        // VERIFIED_DOMAIN, which is a real MX-backed mailbox.
        //
        // `unIdentifiedUser` is exactly the recipients with NO on-chain public key — the ones who cannot
        // receive the encrypted copy and for whom SMTP is the only delivery there is. filterTo/filterCC/
        // filterBcc already have aliased addresses removed (their primary receives instead), so
        // intersecting the two leaves the genuine external recipients and nothing else.
        const isWeb2Only = (email) => !!email && !!unIdentifiedUser?.includes(email);
        // `const`: the visible To is now whatever the user actually addressed and nothing else. Only web2Bcc
        // is ever added to — see the copy-target block below for why nothing may be promoted into To.
        const web2To = filterTo?.filter(isWeb2Only) || [];
        const web2CC = filterCC?.filter(isWeb2Only) || [];
        let web2Bcc = filterBcc?.filter(isWeb2Only) || [];

        // Forward-rule and alias targets with no chain key, so SMTP is the only way to reach them too.
        // These are ADDITIONAL copy targets, which is how the on-chain path treats them as well (their
        // keyed counterparts are concatenated onto filteredTo above). The one REPLACEMENT is the alias, and
        // that has already been applied to filterTo/filterCC/filterBcc — so this is a union, not a naive
        // one: the aliased address is gone from the base set before its primary is added here.
        //
        // Carried as ordinary recipients rather than in the `forwardEmails` field because that field is an
        // envelope OVERRIDE on the server: when it is non-empty the RCPT TO list becomes those addresses
        // ONLY, so the genuine external recipient sitting in `to` silently received nothing. Leaving it
        // unused also keeps this correct whichever way that field is later interpreted — an override of an
        // external-only header set and a union with it deliver exactly the same list.
        const combainedUsers = [...aliasesUnIdentifiedUsers, ...forwardUnIdentifiedUsers];
        const addressed = new Set([...web2To, ...web2CC, ...web2Bcc].map(email => email.toLowerCase()));
        const web2CopyTargets = [];
        for (const email of combainedUsers) {
            // Deduplicated against the addressees and against each other: an address listed twice becomes
            // two RCPT TO commands, which some MTAs deliver twice.
            if (email && !addressed.has(email.toLowerCase())) {
                addressed.add(email.toLowerCase());
                web2CopyTargets.push(email);
            }
        }

        // ALWAYS Bcc, NEVER To. A forward/alias target is somebody's private redirect address and must not
        // be disclosed to the other recipients — MailKit strips the Bcc header before transmission (SmtpClient
        // adds it to HiddenHeaders when it serialises the message) and uses those addresses for RCPT TO only.
        //
        // This used to promote the whole list into the VISIBLE To whenever there was no external addressee,
        // and that list can hold targets belonging to DIFFERENT recipients. With
        // bcc = [bob@gmail (→ y@ext), carl@yahoo (→ z@ext)] it produced `To: y@ext, z@ext`, so carl's private
        // redirect was disclosed to bob and to y@ext, and bob's to carl and to z@ext — addresses that had
        // previously only ever appeared in RCPT TO, mutually invisible. Promoting only a lone target does not
        // fix it either: that one address still lands in a To header read by every other web2 Bcc recipient.
        //
        // The cost is a message with an empty To header. That is the lesser harm, and it already occurs on a
        // bcc-only external send. Delivery is unaffected: DEmailsService.SendEmailAsync builds the SMTP
        // envelope from the parsed to/cc/bcc FORM FIELDS (ResolveRecipientsAsync) and hands that list to
        // SmtpClient.SendAsync(mime, sender, recipients) explicitly — RCPT TO is never read back off the
        // headers, and the empty-envelope guard there counts bcc addresses like any other.
        web2Bcc = web2Bcc.concat(web2CopyTargets);

        const web2Object = [subject, sender, web2To, web2CC, web2Bcc, message, files];
        const hasWeb2Recipients = web2To.length > 0 || web2CC.length > 0 || web2Bcc.length > 0;
        if (!senderName) {
            if (unIdentifiedUser?.length > 0) {
                for (const web2 of unIdentifiedUser) {
                    const status = await domainAvailableCheck(web2);
                    const helperContractMethod = getHostHelperContract();
                    if (!status) {
                        const helperfunctionParams = [sender, unIdentifiedUser];
                        const web2AddressBookForUser = await helperContractMethod.methods.getWeb2Emails(sender).call();
                        const filterAddress = web2AddressBookForUser.filter(email => email === web2);
                        if (filterAddress.length === 0) {
                            // The signer is the wallet ADDRESS. This used to read accounts[0], where
                            // `accounts` was the address STRING from localStorage — so accounts[0] was its
                            // first character, "0". That is truthy, so signWithUserWallet's own recovery
                            // (`if (!sender)` → ask the wallet for its selected account) never fired and the
                            // wallet was handed from: "0", leaving nothing to correct it. The external
                            // address was then never recorded in the sender's web2 address book.
                            await transactionAction(helperContractMethod, "addWeb2Emails", helperfunctionParams, accountAddress, 'helper');
                        }
                        // A keyless recipient does not always mean there is an SMTP copy to send: when all
                        // of them are alias addresses, the mail went to their primaries on-chain and this
                        // set is empty. Posting anyway asks the server to build a MimeMessage with no
                        // recipients. `checkIsSaved` moved inside for the same reason — it means "something
                        // was actually delivered", and on-chain delivery already set it when it happened.
                        if (hasWeb2Recipients) {
                            checkIsSaved = true;
                            await sendWebTwoEmail(web2Object);
                        }
                        break;
                    }
                }
            } else if (forwardUnIdentifiedUsers?.length || aliasesUnIdentifiedUsers?.length) {
                if (hasWeb2Recipients) {
                    checkIsSaved = true;
                    await sendWebTwoEmail(web2Object);
                }
            }
        }
        if (isSavedOn && checkIsSaved) {
            // REFUSE TO ARCHIVE RATHER THAN ARCHIVE A PLACEHOLDER.
            //
            // Reaching here means the mail is already delivered — the addEmails/web2 writes above have
            // landed — so skipping this second, separate transaction costs the recipients nothing. What it
            // avoids is writing `_encryptedData[0] = "MSG"`: an immutable Sent row that looks like a saved
            // message and can never be decrypted, plus the global claim on that string that makes every
            // later user's Sent copy revert (isSenderCopyPlaceholder explains the contract rule).
            //
            // The user is told, because nothing else in this flow would: delivery succeeded, the caller is
            // about to show "Message Sent", and a Sent folder quietly missing an entry is discovered months
            // later or never.
            if (!senderCopyCiphertext) {
                warnSenderCopyNotSaved('the sender copy could not be encrypted');
            } else {
                const functionParams = [[userNameData.name], [senderCopyCiphertext], senderEmailDetails, subject, [...constants.emailAttributes, { key: 'isSaved', value: 'true' }]];
                await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay
                // Same string-indexing bug as addWeb2Emails above — accounts[0] was the character "0", never an
                // address. storeSendMail is what puts the mail in the user's own Sent folder, so with a bogus
                // `from` the message could be delivered yet never appear in Sent. accountAddress (not
                // walletAddress) because this row is stored for the signed-in user — userNameData.name above —
                // even when a senderName was supplied, and walletAddress is deliberately '' in that case.
                const sentCopyHash = await transactionAction(contractMethods, "storeSendMail", functionParams, accountAddress, 'hostContract');
                // transactionAction resolves to null when the write never reached the chain (it catches and
                // reports, including a revert). This return value was discarded, which is precisely why the
                // "MSG" collision was invisible: every affected send reverted here and still reported
                // success. The send itself stays successful — the mail WAS delivered — but the missing
                // archive is now stated instead of assumed.
                if (!sentCopyHash) {
                    warnSenderCopyNotSaved('the storeSendMail transaction did not reach the chain');
                }
            }
        }
        if (!checkIsSaved) {
            toast.error("Something went wrong!", {
                position: "top-center",
                transition: Bounce,
            });
            return false;
        }
        return true;
    } catch (error) {
        console.error('error', error);
        if (error instanceof AttachmentUploadError) {
            // Nothing has been written on-chain at this point, so the send is genuinely aborted, not partial.
            // Name the files: the whole point of failing is that the user finds out BEFORE the mail goes out
            // missing them. Shares a toastId with the caller's generic "send failed" toast so this more
            // specific message is the one the user actually sees — react-toastify drops a toast whose id
            // is already on screen, hence the dismiss first. Dismiss BY ID: a bare toast.dismiss() clears
            // every active toast, killing unrelated messages that have nothing to do with this send.
            toast.dismiss("sendFailure");
            toast.error(
                `Message not sent — these attachments could not be uploaded: ${error.failedFiles.join(", ")}. Please remove or re-attach them and try again.`,
                {
                    toastId: "sendFailure",
                    position: "top-center",
                    transition: Bounce,
                }
            );
        }
        if (error instanceof EncryptionFailedError) {
            // At least one copy of the mail has no ciphertext. This throw happens before saveEmailForUser,
            // so nothing has been written on-chain and the send is genuinely aborted, not partial. Name the
            // addresses: the failure the user has to be protected from is a mail that goes out to everyone
            // EXCEPT one recipient while reporting "Message Sent", so they need to know who is missing.
            // Shared toastId + dismiss-by-id for the same reason as the branches around it: this specific
            // message must be the one on screen, and a bare dismiss() would clear unrelated toasts too.
            toast.dismiss("sendFailure");
            toast.error(
                `Message not sent — it could not be encrypted for: ${error.addresses.join(", ")}. Nothing was sent; your draft is still here, please try again.`,
                {
                    toastId: "sendFailure",
                    position: "top-center",
                    transition: Bounce,
                }
            );
        }
        if (error instanceof EnvelopeV3Error) {
            // The v3 envelope could not be built for at least one recipient. Nothing has been written
            // on-chain, so the send is genuinely aborted. Say so rather than letting the caller's generic
            // "the blockchain transaction failed" message misattribute it — and never retry as v1: a
            // recipient who was promised v3 and handed v1 is not a degraded send, it is a different format.
            toast.dismiss("sendFailure");
            toast.error(
                "Message not sent — the message could not be encrypted for one or more recipients. Please try again.",
                {
                    toastId: "sendFailure",
                    position: "top-center",
                    transition: Bounce,
                }
            );
        }
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '/sendEmail',
            message: `FileName: emailHelper.js, Fun: sendInvite, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        if (!senderName) await setOutBoxDB(outbox);
        return false;
    }
}

/**
 * Park a failed send in the outbox. The body is the user's own plaintext, so it is sealed under the dMail
 * DEK at rest (db/at-rest.js) — it used to sit readable on disk, which only the sign-in wipe had been
 * hiding. Returns false if it could not be stored encrypted; it never falls back to plaintext.
 */
async function setOutBoxDB(json) {
    return await addOutbox(json);
}

async function getEncryptedMessageByUserArray(userArray, subject, message, files, event, fileBase64ByFile = null) {
    const promises = userArray.map(async (user) => {
        if (user) {
            const encryptedMessage = await getEncryptedMessageByUser(user, subject, message, files, event, null, fileBase64ByFile);
            return encryptedMessage || "-";
        }
        return null;
    });

    const results = await Promise.all(promises);
    return results.filter(msg => msg !== null);
}


/**
 * The RECIPIENT path. Everything below is bound to getPublicKeyOfUser(recipient) and must STAY that way,
 * whatever DMAIL_DEK_WRITE says: the recipient does not have our DEK and never will, so a DEK-sealed body or
 * attachment here is unreadable to them forever, with no recovery. isDekWriteEnabled() must never appear in
 * this function.
 *
 * Two recipient formats live here. v3 is the ONLY one written now; v1 remains solely so historical mail
 * can still be READ:
 *
 *  v1 (READ ONLY — no longer produced) — the whole body, and each whole attachment, encrypted directly
 *      to the recipient's public key. On receive the ENTIRE ciphertext has to go through the wallet,
 *      which is why mobile could never open a v1 attachment: it exceeds Android's Binder limit.
 *
 *  v3 — one content key K per recipient copy; the body and every attachment for THIS recipient are sealed
 *      symmetrically under K, and K is wrapped once to the recipient's public key and prefixed onto each
 *      blob. Both operations are local WASM; the wallet is not involved in sending at all, exactly as
 *      before. On receive the wallet opens only the 2596-char wrapped K.
 *
 * K IS PER (EMAIL, RECIPIENT), NOT PER EMAIL. One K per recipient copy is the whole point: a K shared across
 * the recipient set would let any recipient open the copy addressed to any other, which reveals the BCC list
 * that emailHeaderObject deliberately strips. Body and attachments WITHIN one recipient's copy do share K —
 * that is what lets a reader open the attachments after a single wallet round-trip.
 */
async function getEncryptedMessageByUser(user, subject, message, files, event, forwardUser = null, fileBase64ByFile = null) {
    try {
        const publicKey = await contractMethods.methods.getPublicKeyOfUser(forwardUser || user).call();
        const emailObject = { recipient: user, subject: subject, message: message, event: event };
        if (!publicKey) return null;

        if (isV3WriteEnabled()) {
            // Mint and wrap BEFORE anything is uploaded. wrapK asserts the 2596-char length, so a bad public
            // key or a crypto fault fails here — with nothing written to S3 and nothing written on-chain —
            // rather than after half the attachments are already up.
            const k = mintK();
            const wrapped = await wrapK(publicKey, k);
            if (files && files.length) {
                const attachmentResult = await encryptFilesV3(files, wrapped, k, fileBase64ByFile);
                emailObject.attachment = attachmentResult.returnValue;
            }
            const encryptedData = await sealV3(wrapped, k, JSON.stringify(emailObject));
            // The envelope version is written LITERALLY. Never forward the crypto layer's version: symEncrypt
            // always reports 'v1', and a v3 blob tagged 'v1' sends a legacy reader into an asymmetric unwrap
            // of a v3 payload, which panics the Go runtime rather than returning an error.
            const data = await getEncryptedValue({ encryptedData, version: ENVELOPE_VERSION_V3 });
            // K goes out of scope here and is never persisted, logged or transmitted in any other form.
            // This recipient HAS an on-chain key, so there is no legitimate way to produce no ciphertext for
            // them: anything short of a real one means dropping them from a send reported as successful.
            return requireEncryptedValue(data, forwardUser || user);
        }

        if (files && files.length) {
            const attachmentResult = await sendAttachment(files, publicKey);
            emailObject.attachment = attachmentResult.returnValue;
        }
        const msg = JSON.stringify(emailObject);
        const encryptMsg = await encryptText(msg, publicKey);
        const data = await getEncryptedValue(encryptMsg);
        // Same rule as the v3 branch above: a failed call must abort the send, not silently remove this
        // recipient from it.
        return requireEncryptedValue(data, forwardUser || user);
    } catch (err) {
        // An attachment that failed to upload must NOT be swallowed here. Returning undefined turns into "-"
        // upstream, which silently drops this recipient (or sends them a mail missing files) while the user
        // is still told "Message Sent". Rethrow so sendEmails aborts the whole send and names the files.
        if (err instanceof AttachmentUploadError) throw err;
        // Same reasoning, and stricter: the v3 path may not fall back to v1, to the DEK or to plaintext, so
        // any envelope failure has to abort the send instead of quietly dropping this recipient.
        if (err instanceof EnvelopeV3Error) throw err;
        // Same reasoning again for a /getEncryptValue call that did not complete. Swallowing it here is what
        // turned a transient 429 into a recipient quietly deleted from a mail reported as "Message Sent".
        if (err instanceof EncryptionFailedError) throw err;
        console.log(err);
    }
}

async function saveEmailForUser(account, functionParams) {
    const contractMethods = contractCall(contractData.contract, contractAddress);
    const txHash = await transactionAction(contractMethods, "addEmails", functionParams, account, 'hostContract');
    return txHash;
}

async function getChainDetails(receiptDomain) {
    try {
        const hostAddress = await contractMethods.methods.hostRegistryAddress().call();
        const hostContractMethods = contractCall(hostRegistry.contract, hostAddress);
        const chainJson = await hostContractMethods.methods.getChainDetails(receiptDomain).call();
        return chainJson;
    } catch (err) {
        console.log(err);
    }
}

async function getOwnDomainBoolStatus(userArray) {

    let present = false;
    for (const users of userArray) {
        for (const user of users) {
            const publicKey = await contractMethods.methods.getPublicKeyOfUser(user).call();
            if (publicKey) {
                present = true;
                break;
            }
        }
    }

    return present;
}

async function getChainDomainJson(userArray) {

    const hostDetails = [];
    const domain = await contractMethods.methods.constDomain().call();

    for (const users of userArray) {
        for (const user of users) {
            const userDomain = user.split("@")[1];
            if (userDomain && userDomain != domain) {
                const chainJson = await getChainDetails(userDomain);
                if (chainJson["0"] && chainJson["1"]) {
                    chainJson["user_domain"] = userDomain;
                    hostDetails.push(chainJson);
                }
            }
        }
    }

    return hostDetails;
}

async function domainAvailableCheck(user) {
    const publicKey = await contractMethods.methods.getPublicKeyOfUser(user).call();;
    if (publicKey) {
        return true;
    } else {
        return false;
    }
}


async function userAvailableCheck(userList) {
    try {
        const unIdentifiedUser = [];
        for (let user of userList) {
            const publicKey = await contractMethods.methods.getPublicKeyOfUser(user).call();
            if (!publicKey) {
                unIdentifiedUser.push(user);
            }
        }
        return unIdentifiedUser
    } catch (err) {
        console.log(err);
    }
}

/** Returns true when the delete/restore actually landed, false when the chain write failed. */
export const deleteEmails = async (message, msgId, index, messageList, type, getFolderIndex) => {
    const user = cookies.get("userObject");

    switch (type) {
        case "Draft":
            return await deleteDraft(user.name, msgId);
        case "Outbox":
            return await deleteOutbox(index);
        case "Insecure":
            return await deleteInsecure(msgId);
        case "Trash":
            return await deleteTrashMail(user.name, msgId, message);
        case "Sent":
            return await deleteSentMail(user.name, msgId);
        case "Folders":
            return await deleteFolderMail(index, message, user.name, user.wallet, getFolderIndex);
        case "Inbox":
        case "Archive":
        case "Important":
            return await deleteInboxMail(user.name, msgId);
        case "inboxtrash":
        case "Senttrash":
            return await restoreEmail(user.name, message, type);
        case "emptyTrash":
            return await emptyTrash(message, user.name);
        default:
            return true;
    }
}
// Every helper below returns true only when the work actually landed. A chain write
// that never made it resolves to null in transactionAction, and callers depend on
// that reaching them so they can tell the user instead of silently reverting.
async function emptyTrash(message, userName) {
    const inboxData = message.map(value => value.id).map(value => parseInt(value.id));
    const collection = [...inboxData];
    return !!await permanentlyDeleteEmail(userName, collection, 'hostContract')
}

async function restoreEmail(userName, message, type) {
    if (type === 'inboxtrash') {
        let functionParams = "";
        if (message.length > 1) {
            const inboxdata = message.map(data => parseInt(data.id))
            functionParams = [userName, inboxdata, [{ key: 'isDeleted', value: 'false' }]];
        }
        else {
            let value = ""
            if (Array.isArray(message) && message.length > 0) {
                value = parseInt(message[0].id);
            }
            else {
                value = parseInt(message.id)
            }
            functionParams = [userName, [value], [{ key: 'isDeleted', value: 'false' }]];
        }
        return await updateEmailAttributeValue(...functionParams, 'hostContract');
    } else if (type === 'Senttrash') {

        let functionParams = "";
        if (message.length > 1) {
            const inboxdata = message.map(data => parseInt(data.id))

            functionParams = [userName, inboxdata, [{ key: "isDeleted", value: "false" }]];
        }
        else {
            let value = ""
            if (Array.isArray(message) && message.length > 0) {
                value = parseInt(message[0].id);
            }
            else {
                value = parseInt(message.id)
            }
            functionParams = [userName, [value], [{ key: "isDeleted", value: "false" }]];
        }
        return await updateEmailAttributeValue(...functionParams, 'hostContract');
    }
    return true; // unknown type: nothing to do, not a failure
}

// Local/Web2 deletes touch no contract, so they cannot "fail to land" on-chain.
async function deleteDraft(userName, id) {
    await db.table('draft').delete(id);
    return true;
}

// `index` is a position in the list the UI rendered, so it MUST be resolved against the same
// owner-filtered ordering the readers use — see at-rest.listOutbox().
async function deleteOutbox(index) {
    return await deleteOutboxAt(index);
}

async function deleteInsecure(id) {
    await updateInsecureMail('deleteemail', id, "");
    return true;
}

const deleteTrashMail = async (userName, msgId, message) => {
    const response = message && Array.isArray(message) && message?.filter(data => data?.emailType === 'Senttrash');
    if (response.length > 0) {
        const inboxData = message?.length > 1
            ? message?.map(data => parseInt(data?.id))
            : [parseInt(message?.[0]?.id || message?.id)];
        return !!await permanentlyDeleteEmail(userName, inboxData, 'hostContract');
    } else {
        return !!await permanentlyDeleteEmail(userName, [parseInt(msgId)], 'hostContract');
    }
}

const deleteSentMail = async (userName, msgId) => {
    const attribute = [{ key: "isDeleted", value: "true" }];
    const functionParams = msgId.length > 1
        ? [userName, msgId?.map(data => parseInt(data)), attribute]
        : [userName, [Array.isArray(msgId) ? parseInt(msgId[0]) : parseInt(msgId)], attribute];

    return await updateEmailAttributeValue(...functionParams, 'hostContract');
}

// const deleteFolderMail = async (index, messageList, userName, wallet, getFolderIndex) => {
//     const response = messageList.filter(data => data.emailType === "inbox")
//     const sentresponse = messageList.filter(data => data.emailType === "sent")
//     if (response.length > 0) {
//         const value = response.map(data => data.id)
//         await deleteInboxMail(userName, value);
//     }
//     if (sentresponse.length > 0) {
//         const value = sentresponse.map(data => data.id)
//         await deleteSentMail(userName, value);
//     }

// }
const deleteFolderMail = async (index, messageList, userName, wallet, getFolderInde) => {
    try {
        const inboxIds = [];
        const sentIds = [];
        messageList.forEach((data) => {
            if (data.emailType === "inbox") {
                inboxIds.push(data.id);
            } else if (data.emailType === "sent") {
                sentIds.push(data.id);
            }
        });
        let ok = true;
        if (inboxIds.length > 0) {
            ok = await deleteInboxMail(userName, inboxIds) && ok;
        }
        if (sentIds.length > 0) {
            ok = await deleteSentMail(userName, sentIds) && ok;
        }
        return ok;
    } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '-',
            message: `FileName: emailHelper.js, Fun: deleteFolderMail, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        console.error('Error deleting messages:', error);
        return false;
    }
};

const deleteInboxMail = async (userName, msgId) => {
    let functionParams = ""
    if (msgId.length > 1) {
        functionParams = [userName, msgId, [{ key: "isDeleted", value: "true" }]];
    } else {
        functionParams = [userName, [parseInt(msgId)], [{ key: "isDeleted", value: "true" }]];
    }
    return await updateEmailAttributeValue(...functionParams, 'hostContract');
}

export const updateAttribute = async (userName, id, key, type, value) => {

    const functionParams = [userName, id, type, key, value];
    const accountAddress = localStorage.getItem("walletAddress");
    const helperContractMethod = getHostHelperContract();
    await transactionAction(helperContractMethod, "setAttribute", functionParams, accountAddress, 'helper');
}

export const organizationEmailValidation = async (allUsers) => {
    try {
        // Strict read: this decides whether an org RESTRICTION applies, so it must distinguish
        // "no organization" (null -> genuinely unrestricted) from "org row exists but unreadable"
        // (throws -> handled below). The non-strict read collapses both to null, which would let a
        // key hiccup silently disable the restriction.
        const organizationData = await readOrganizationDataStrict();
        if (organizationData && (!organizationData?.organizationData?.organizationSettings?.sendOutsideOrg && !organizationData?.userSettingsData?.sendOutsideOrg)) {
            const usersDomain = allUsers.map(data => data.split("@")[1]);
            return usersDomain?.some(data => data !== organizationData?.organizationData?.domain)
        } else {
            return false
        }
    } catch (error) {
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '-',
            message: `FileName: emailHelper.js, Fun: organizationEmailValidation, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        // FAIL CLOSED. Reaching here means an organization row exists but could not be opened, so we
        // cannot prove this send is permitted. Returning false would let a transient key failure wave
        // through exactly the sends the org configured itself to block. Returning true reports the
        // send as restricted; the user retries once the wallet/DEK is available.
        return true
    }
}

export const forwardEmailsCheck = async (allUsers) => {
    const forwardEmails = []
    const aliasesEmails = []
    try {
        if (allUsers?.length) {
            const forwardEmailsResult = await getForwardEmailDetails();
            if (forwardEmailsResult?.data?.forward_email_data?.length > 0) {
                const forwardEmail = forwardEmailsResult?.data?.forward_email_data;
                const ruleEmails = forwardEmail?.map(rule => rule?.fromEmail?.toLowerCase()) || [];
                const filterForwards = allUsers?.filter(email => ruleEmails.includes(email?.toLowerCase()));
                const filterForwardRules = forwardEmail?.filter(rule => filterForwards?.includes(rule?.fromEmail?.toLowerCase()))?.map(user => user?.forwardEmail) || [];
                forwardEmails.push(...filterForwardRules);
            }

            if (forwardEmailsResult?.data?.aliases_email_data?.length > 0) {
                const aliasesEmailsData = forwardEmailsResult?.data?.aliases_email_data;
                for (const user of allUsers) {
                    for (const aliasGroup of aliasesEmailsData) {
                        if (aliasGroup?.aliases.some(a => a.email === user)) {
                            aliasesEmails.push({ email: user, primary: aliasGroup.email });
                            break;
                        }
                    }
                }
            }
        }
        return { forwardEmails, aliasesEmails };
    } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '/dsuite/fetchForwardEmailSettings',
            message: `FileName: emailHelper.js, Fun: forwardEmailsCheck, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        console.log('*****Error on forwardEmailsCheck*****', error);
        return { forwardEmails, aliasesEmails };
    }
}

export const permanentlyDeleteEmail = async (userName, emailIds, contractName) => {
    const functionParams = [userName, emailIds];
    const accountAddress = localStorage.getItem("walletAddress");
    const contractMethods = getHostContract();
    return await transactionAction(contractMethods, "permanentlyDeleteEmail", functionParams, accountAddress, contractName);
}

export const updateEmailAttributeValue = async (userName, emailIds, attributes, contractName) => {
    try {
        const web2EmailIds = [];
        const web3EmailIds = [];
        
        emailIds.forEach(id => {
            const idStr = String(id);
            if (/[a-f-]/i.test(idStr)) {
                web2EmailIds.push(id);
            } else {
                web3EmailIds.push(parseInt(id));
            }
        });
        
        if (web3EmailIds.length > 0) {
            const functionParams = [userName, web3EmailIds, attributes];
            const accountAddress = localStorage.getItem("walletAddress");
            const contractMethods = getHostContract();
            const hash = await transactionAction(contractMethods, "updateEmailAttributes", functionParams, accountAddress, contractName);
            // transactionAction resolves to null when the write never reached the chain.
            // Report that to the caller — returning true here made every failed star /
            // archive / move look like a success and silently revert in the UI.
            if (!hash) return false;
        }

        if (web2EmailIds.length > 0) {
            for (const attr of attributes) {
                if (attr.key === 'isRead') {
                    await updateInsecureMail('isRead', web2EmailIds, attr.value === 'true' ? 1 : 0);
                } else if (attr.key === 'isArchive') {
                    await updateInsecureMail('isArchive', web2EmailIds, attr.value === 'true' ? 1 : 0);
                } else if (attr.key === 'folder') {
                    await updateInsecureMail('folder', web2EmailIds, attr.value);
                }
            }
        }
        
        return true;
    } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '-',
            message: `FileName: emailHelper.js, Fun: updateEmailAttributeValue, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        console.log('*****Error on UpdateEmailAttributeValue*****', error);
        return false;
    }
}

export async function getEmails(user) {
    try {
        const totalCount = await contractMethods.methods.getEmailCount(user).call();
        // Ethers returns a BigNumber, convert it to a regular number for loops
        const totalCountNum = Number(totalCount);
        if (totalCountNum === 0) {
            console.log("No emails found for this user.");
            return []; // Return an empty array immediately
        }
        const allEmails = [];
        const count = 500;
        // 2. Loop until we have fetched all emails
        for (let offset = 0; offset < totalCountNum; offset += count) {
            const pageOfEmails = await contractMethods.methods.getEmailList(
                user,
                offset,
                count
            ).call();

            allEmails.push(...pageOfEmails);
        }
        return allEmails;
    } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '-',
            message: `FileName: emailHelper.js, Fun: getEmails, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        console.error("Failed to fetch email:", error);
        throw error;
    }
}

export async function getSentEmails(user) {
    try {
        const totalCount = await contractMethods.methods.getEmailCount(user).call();
        // Ethers returns a BigNumber, convert it to a regular number for loops
        const totalCountNum = Number(totalCount);
        if (totalCountNum === 0) {
            console.log("No emails found for this user.");
            return []; // Return an empty array immediately
        }
        const allEmails = [];
        const count = 500;
        // 2. Loop until we have fetched all emails
        for (let offset = 0; offset < totalCountNum; offset += count) {
            const pageOfEmails = await contractMethods.methods.getSentEmailList(
                user,
                offset,
                count
            ).call();

            allEmails.push(...pageOfEmails);
        }
        return allEmails;
    } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '-',
            message: `FileName: emailHelper.js, Fun: getSentEmails, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        console.error("Failed to fetch email:", error);
        throw error;
    }
}